// Transcripts Page - Updated
import { useState, useEffect } from 'react';
import Footer from '../home/components/Footer';

interface Transcript {
  id: string;
  title: string;
  type: 'audio' | 'video';
  duration: string;
  date: string;
  description: string;
  topics: string[];
  url: string;
  wordCount: number;
}

export default function TranscriptsPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<'all' | 'audio' | 'video'>('all');

  const transcripts: Transcript[] = [
    {
      id: 'audio-1',
      title: 'Constitutional AI versus the Ghost of PACE',
      type: 'audio',
      duration: '38:16',
      date: '2024-12-15',
      description: 'Deep dive into Constitutional AI frameworks and the PACE lawsuit implications for AI liability',
      topics: ['Constitutional AI', 'PACE Lawsuit', 'AI Liability', 'Legal Framework', 'Behavioral Mimicry'],
      url: '/publications#audio-1',
      wordCount: 7500
    },
    {
      id: 'audio-2',
      title: 'Existing Law Forces AI Transparency and Design',
      type: 'audio',
      duration: '45:30',
      date: '2024-12-20',
      description: 'Comprehensive analysis of how existing legal frameworks mandate AI transparency and constitutional design principles',
      topics: ['Legal Framework', 'AI Transparency', 'Constitutional Design', 'Regulatory Compliance', 'Design Standards'],
      url: '/transcript-audio-10',
      wordCount: 8200
    },
    {
      id: 'video-1',
      title: 'The Survival Imperative: Why Constitutional AI is Non-Negotiable',
      type: 'video',
      duration: '22:45',
      date: '2024-12-10',
      description: 'Comprehensive overview of why Constitutional AI is essential for AI safety and accountability',
      topics: ['AI Safety', 'Constitutional AI', 'NIST Framework', 'Governance'],
      url: '/publications#video-1',
      wordCount: 5200
    },
    {
      id: 'video-2',
      title: 'Engineering Trust: From Black Box to Glass Box AI',
      type: 'video',
      duration: '18:30',
      date: '2024-12-08',
      description: 'Technical deep dive into transforming opaque AI systems into transparent, auditable solutions',
      topics: ['Glass Box AI', 'Transparency', 'Auditability', 'Technical Architecture'],
      url: '/publications#video-2',
      wordCount: 4100
    },
    {
      id: 'video-3',
      title: 'The Mirror Effect: Why AI Mimics Our Worst Habits',
      type: 'video',
      duration: '16:20',
      date: '2024-12-05',
      description: 'Analysis of behavioral mimicry in AI systems and the digital mirror problem',
      topics: ['Behavioral Mimicry', 'AI Bias', 'Mirror Effect', 'Design Defects'],
      url: '/publications#video-3',
      wordCount: 3800
    }
  ];

  const filteredTranscripts = transcripts.filter(transcript => {
    const matchesSearch = transcript.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         transcript.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         transcript.topics.some(topic => topic.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesFilter = filterType === 'all' || transcript.type === filterType;
    return matchesSearch && matchesFilter;
  });

  const totalWordCount = transcripts.reduce((sum, t) => sum + t.wordCount, 0);

  useEffect(() => {
    console.log('=== TRANSCRIPT DEBUG ===');
    console.log('Total transcripts:', transcripts.length);
    console.log('Audio 2 data:', transcripts.find(t => t.id === 'audio-2'));
    console.log('Filtered transcripts:', filteredTranscripts.length);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      
      <div className="container mx-auto px-6 lg:px-12 py-20 max-w-7xl">
        {/* Header */}
        <header className="mb-16 text-center max-w-4xl mx-auto">
          <h1 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-6" style={{ fontFamily: "'Inter', sans-serif" }}>
            Audio & Video Transcripts
          </h1>
          <p className="text-xl text-slate-600 mb-4">
            Searchable, indexable transcripts of all our research discussions and presentations
          </p>
          <div className="flex items-center justify-center gap-6 text-sm text-slate-500">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 flex items-center justify-center">
                <i className="ri-file-text-line text-lg text-red-800"></i>
              </div>
              <span>{transcripts.length} Transcripts</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 flex items-center justify-center">
                <i className="ri-text text-lg text-red-800"></i>
              </div>
              <span>{totalWordCount.toLocaleString()} Words</span>
            </div>
          </div>
        </header>

        {/* Search and Filter */}
        <div className="mb-12 max-w-4xl mx-auto">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search Bar */}
            <div className="flex-1 relative">
              <div className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 flex items-center justify-center">
                <i className="ri-search-line text-xl text-slate-400"></i>
              </div>
              <input
                type="text"
                placeholder="Search transcripts by title, topic, or keyword..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-800 focus:border-transparent text-sm"
              />
            </div>

            {/* Filter Buttons */}
            <div className="flex gap-2">
              <button
                onClick={() => setFilterType('all')}
                className={`px-6 py-3 font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer text-sm rounded-lg ${
                  filterType === 'all'
                    ? 'bg-red-800 text-white'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                All
              </button>
              <button
                onClick={() => setFilterType('audio')}
                className={`px-6 py-3 font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer text-sm rounded-lg ${
                  filterType === 'audio'
                    ? 'bg-red-800 text-white'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                Audio
              </button>
              <button
                onClick={() => setFilterType('video')}
                className={`px-6 py-3 font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer text-sm rounded-lg ${
                  filterType === 'video'
                    ? 'bg-red-800 text-white'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                Video
              </button>
            </div>
          </div>
        </div>

        {/* Results Count */}
        {searchQuery && (
          <div className="mb-6 text-center text-slate-600">
            Found {filteredTranscripts.length} transcript{filteredTranscripts.length !== 1 ? 's' : ''} matching "{searchQuery}"
          </div>
        )}

        {/* Transcripts Grid */}
        <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {filteredTranscripts.map((transcript) => (
            <div
              key={transcript.id}
              className={`bg-white border-2 rounded-xl p-8 hover:shadow-2xl transition-all duration-300 group flex flex-col ${
                transcript.id === 'audio-2' 
                  ? 'border-red-800 ring-4 ring-red-100 shadow-xl' 
                  : 'border-slate-200'
              }`}
            >
              {/* Special Badge for Audio 2 */}
              {transcript.id === 'audio-2' && (
                <div className="mb-4 -mt-2 -mx-2">
                  <span className="inline-block px-4 py-2 bg-red-800 text-white text-xs font-bold rounded-lg">
                    ⭐ FEATURED TRANSCRIPT
                  </span>
                </div>
              )}

              {/* Type Badge */}
              <div className="flex items-center justify-between mb-4">
                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                  transcript.type === 'audio'
                    ? 'bg-blue-100 text-blue-800'
                    : 'bg-purple-100 text-purple-800'
                }`}>
                  {transcript.type === 'audio' ? (
                    <>
                      <i className="ri-mic-line mr-1"></i>
                      Audio Discussion
                    </>
                  ) : (
                    <>
                      <i className="ri-video-line mr-1"></i>
                      Video Presentation
                    </>
                  )}
                </span>
                <span className="text-sm text-slate-500">{transcript.duration}</span>
              </div>

              {/* Title */}
              <h3 className="text-xl font-bold text-slate-900 mb-3 group-hover:text-red-800 transition-colors duration-300">
                {transcript.title}
              </h3>

              {/* Description */}
              <p className="text-slate-600 mb-4 text-sm leading-relaxed">
                {transcript.description}
              </p>

              {/* Topics */}
              <div className="flex flex-wrap gap-2 mb-6">
                {transcript.topics.slice(0, 3).map((topic, index) => (
                  <span
                    key={index}
                    className="px-3 py-1 bg-slate-100 text-slate-700 rounded-full text-xs font-medium"
                  >
                    {topic}
                  </span>
                ))}
                {transcript.topics.length > 3 && (
                  <span className="px-3 py-1 bg-slate-100 text-slate-700 rounded-full text-xs font-medium">
                    +{transcript.topics.length - 3} more
                  </span>
                )}
              </div>

              {/* Footer with metadata and button */}
              <div className="mt-auto pt-6 border-t-2 border-slate-100">
                <div className="flex items-center gap-4 text-xs text-slate-500 mb-4">
                  <span className="font-medium">{new Date(transcript.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                  <span className="font-medium">{transcript.wordCount.toLocaleString()} words</span>
                </div>
                
                {/* View Full Transcript Button - SUPER PROMINENT */}
                <a
                  href={transcript.url}
                  className={`flex items-center justify-center gap-3 w-full px-8 py-4 text-white text-base font-bold rounded-xl hover:scale-105 transition-all duration-300 cursor-pointer whitespace-nowrap shadow-lg hover:shadow-xl ${
                    transcript.id === 'audio-2' 
                      ? 'bg-red-800 hover:bg-red-900 animate-pulse' 
                      : 'bg-red-800 hover:bg-red-900'
                  }`}
                  style={{ minHeight: '56px' }}
                >
                  <i className="ri-file-text-line text-xl"></i>
                  <span>VIEW FULL TRANSCRIPT</span>
                  <i className="ri-arrow-right-line text-xl"></i>
                </a>
              </div>
            </div>
          ))}
        </div>

        {/* No Results */}
        {filteredTranscripts.length === 0 && (
          <div className="text-center py-12 bg-slate-50 rounded-lg max-w-2xl mx-auto">
            <i className="ri-search-line text-6xl text-slate-300 mb-4"></i>
            <p className="text-xl text-slate-600">No transcripts found</p>
            <p className="text-slate-500 mt-2">Try adjusting your search or filter</p>
          </div>
        )}

        {/* SEO Benefits Section */}
        <section className="mt-20 max-w-4xl mx-auto bg-slate-50 rounded-lg p-8">
          <h2 className="text-2xl font-bold text-slate-900 mb-6 text-center">
            Why We Provide Full Transcripts
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="flex items-center justify-center w-12 h-12 bg-red-100 rounded-lg mx-auto mb-4">
                <i className="ri-search-eye-line text-2xl text-red-800"></i>
              </div>
              <h3 className="font-bold text-slate-900 mb-2">Searchable</h3>
              <p className="text-sm text-slate-600">Find specific topics and quotes instantly</p>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center w-12 h-12 bg-red-100 rounded-lg mx-auto mb-4">
                <i className="ri-accessibility-line text-2xl text-red-800"></i>
              </div>
              <h3 className="font-bold text-slate-900 mb-2">Accessible</h3>
              <p className="text-sm text-slate-600">Available to all users regardless of ability</p>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center w-12 h-12 bg-red-100 rounded-lg mx-auto mb-4">
                <i className="ri-translate-line text-2xl text-red-800"></i>
              </div>
              <h3 className="font-bold text-slate-900 mb-2">Translatable</h3>
              <p className="text-sm text-slate-600">Easily translate to any language</p>
            </div>
          </div>
        </section>
      </div>

      <Footer />
    </div>
  );
}
